import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { api } from "../swof/api";
import { AdminShell } from "./ProtectedRoute";
import {
  Duim, Foutmelding, Sleeplijst, Titel, Uploader, knopStijl, invoerStijl, wegStijl,
  mediaKaart, useAdminContent,
} from "./bouwstenen";

/* ————— Mediabibliotheek ————— */
export function MediaPage() {
  const { content, herlaad, fout, melden } = useAdminContent();
  const [zoek, setZoek] = useState("");
  const [soort, setSoort] = useState("");

  if (!content) return <AdminShell title="Media"><div data-testid="media-laden">Laden…</div></AdminShell>;

  const lijst = content.media.filter((m) =>
    m.bestandsnaam.toLowerCase().includes(zoek.toLowerCase()) && (!soort || m.soort === soort));

  const gebruikt = new Set([
    ...content.blocks.map((b) => b.inhoud.logo_id),
    ...content.blocks.flatMap((b) => b.inhoud.foto_ids || []),
    ...content.inductees.map((i) => i.portret_id),
    ...content.bestuursleden.map((b) => b.portret_id),
    ...content.partners.map((p) => p.logo_id),
    ...content.galerij_fotos.map((f) => f.media_id),
    ...(content.inductee_fotos || []).map((f) => f.media_id),
    ...content.videos.map((v) => v.poster_id),
  ].filter(Boolean));

  const verwijder = async (m) => {
    if (!window.confirm(`${m.bestandsnaam} verwijderen?`)) return;
    try {
      await api.delete(`/admin/media/${m.id}`);
      await herlaad();
    } catch (e) { melden(e); }
  };

  const wijzigAlt = async (m) => {
    const alt = window.prompt("Alt-tekst", m.alt || "");
    if (alt === null) return;
    try {
      await api.put(`/admin/media/${m.id}`, { alt });
      await herlaad();
    } catch (e) { melden(e); }
  };

  return (
    <AdminShell title="Media" actions={<Uploader testId="media-upload" onKlaar={herlaad} soort="sfeerfoto" />}>
      <Titel onder="Alles wat geüpload is. Verwijderen kan alleen als een bestand nergens meer in gebruik is; anders melden we waar het gebruikt wordt.">
        Mediabibliotheek
      </Titel>
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <input placeholder="Zoek op bestandsnaam" value={zoek} data-testid="media-zoek"
          onChange={(e) => setZoek(e.target.value)} style={{ ...invoerStijl, maxWidth: 320 }} />
        <select value={soort} onChange={(e) => setSoort(e.target.value)} data-testid="media-filter"
          style={{ ...invoerStijl, maxWidth: 220 }}>
          {[["", "Alle soorten"], ["portret", "Portret"], ["sfeerfoto", "Sfeerfoto"], ["logo", "Logo"]]
            .map(([v, l]) => <option key={v} value={v} style={{ color: "#111" }}>{l}</option>)}
        </select>
      </div>
      <Foutmelding fout={fout} />
      <div data-testid="media-grid" style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16,
      }}>
        {lijst.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} style={{ border: "1px solid var(--line)", padding: 12 }}>
            <div style={{ aspectRatio: "3 / 2", background: "#1a1611", overflow: "hidden", marginBottom: 10 }}>
              <img src={m.varianten?.["400"] || m.url} alt={m.alt || ""}
                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ fontFamily: "var(--sans)", fontSize: 12, opacity: 0.8, wordBreak: "break-all" }}>
              {m.bestandsnaam}
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.14em", opacity: 0.5, margin: "6px 0 10px" }}>
              {m.soort} · {m.breedte}×{m.hoogte}
              {gebruikt.has(m.id) && (
                <span data-testid={`media-in-gebruik-${m.id}`} style={{ color: "var(--gold)" }}> · in gebruik</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => wijzigAlt(m)} data-testid={`media-alt-${m.id}`} style={{ ...knopStijl, padding: "7px 10px", fontSize: 9 }}>Alt</button>
              <button onClick={() => verwijder(m)} data-testid={`media-weg-${m.id}`} style={wegStijl}>Weg</button>
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}

/* ————— Video's ————— */
export function VideosPage() {
  const { content, herlaad, fout, melden } = useAdminContent();
  const [bron, setBron] = useState("youtube");
  const [invoer, setInvoer] = useState("");
  const [titel, setTitel] = useState("");
  const [streamAan, setStreamAan] = useState(false);
  const [voortgang, setVoortgang] = useState(null);
  const videoInvoer = React.useRef(null);

  React.useEffect(() => {
    api.get("/admin/stream/status").then(({ data }) => setStreamAan(data.actief)).catch(() => {});
  }, []);

  if (!content) return <AdminShell title="Video's"><div data-testid="videos-laden">Laden…</div></AdminShell>;

  const toevoegen = async () => {
    try {
      await api.post("/admin/videos", { bron, invoer, titel });
      setInvoer(""); setTitel("");
      await herlaad();
    } catch (e) { melden(e); }
  };

  const naarStream = async (e) => {
    const bestand = e.target.files?.[0];
    videoInvoer.current.value = "";
    if (!bestand) return;
    setVoortgang(0);
    try {
      const { data } = await api.post("/admin/stream/upload-url", { titel: titel || bestand.name });
      const form = new FormData();
      form.append("file", bestand);
      await axios.post(data.upload_url, form, {
        onUploadProgress: (p) => setVoortgang(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      await api.post("/admin/stream/klaar", { uid: data.uid, titel: titel || bestand.name });
      setTitel("");
      await herlaad();
    } catch (err) { melden(err); } finally { setVoortgang(null); }
  };

  const verwijder = async (v) => {
    if (!window.confirm(`Video “${v.titel}” verwijderen?`)) return;
    try {
      await api.delete(`/admin/videos/${v.id}`);
      await herlaad();
    } catch (e) { melden(e); }
  };

  const verversStatus = async (v) => {
    try {
      await api.post("/admin/stream/klaar", { uid: v.externe_id, titel: v.titel });
      await herlaad();
    } catch (e) { melden(e); }
  };

  return (
    <AdminShell title="Video's" actions={
      <>
        <button onClick={() => videoInvoer.current.click()} data-testid="stream-upload"
          disabled={!streamAan || voortgang !== null} style={{
            ...knopStijl, opacity: streamAan ? 1 : 0.45,
          }} title={streamAan ? "Videobestand naar Cloudflare Stream uploaden"
            : "Cloudflare Stream is nog niet ingesteld"}>
          {voortgang !== null ? `Uploaden ${voortgang}%` : "Video uploaden"}
        </button>
        <input ref={videoInvoer} type="file" accept="video/*" onChange={naarStream}
          style={{ display: "none" }} data-testid="stream-upload-input" />
      </>
    }>
      <Titel onder="Plak een YouTube-link — het video-id wordt er automatisch uit gehaald — of vul de video-uid van Cloudflare Stream in. Met ‘Video uploaden’ gaat een bestand rechtstreeks naar Cloudflare Stream, niet langs onze eigen server.">
        Video's
      </Titel>
      <div style={{
        display: "grid", gridTemplateColumns: "170px 1fr 1fr auto", gap: 12,
        alignItems: "center", marginBottom: 28,
      }}>
        <select value={bron} onChange={(e) => setBron(e.target.value)} data-testid="video-bron" style={invoerStijl}>
          <option value="youtube" style={{ color: "#111" }}>YouTube</option>
          <option value="cloudflare" style={{ color: "#111" }}>Cloudflare Stream</option>
        </select>
        <input value={invoer} onChange={(e) => setInvoer(e.target.value)} data-testid="video-invoer"
          placeholder={bron === "youtube" ? "https://youtu.be/…" : "Cloudflare video-uid"} style={invoerStijl} />
        <input value={titel} onChange={(e) => setTitel(e.target.value)} data-testid="video-titel"
          placeholder="Titel" style={invoerStijl} />
        <button onClick={toevoegen} data-testid="video-toevoegen" style={knopStijl}>Toevoegen</button>
      </div>
      <Foutmelding fout={fout} />
      <ul data-testid="video-lijst" style={{ listStyle: "none", padding: 0, margin: 0, borderTop: "1px solid var(--line)" }}>
        {content.videos.map((v) => (
          <li key={v.id} data-testid={`video-${v.id}`} style={{
            display: "grid", gridTemplateColumns: "150px 1fr 220px 150px 80px", gap: 16,
            alignItems: "center", padding: "14px 8px", borderBottom: "1px solid var(--line)",
          }}>
            <span style={{
              fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.18em",
              textTransform: "uppercase", color: "var(--gold)",
            }}>{v.bron === "youtube" ? "YouTube" : "Cloudflare"}</span>
            <span style={{ fontFamily: "var(--serif)", fontSize: 18 }}>{v.titel}</span>
            <span style={{ fontFamily: "var(--mono)", fontSize: 11, opacity: 0.6 }}>{v.externe_id}</span>
            <span data-testid={`video-status-${v.id}`} style={{ fontFamily: "var(--mono)", fontSize: 11, opacity: 0.7 }}>
              {v.bron === "cloudflare" ? (
                <>
                  {v.status === "ready" ? "gereed" : v.status === "inprogress" ? "verwerken…" : (v.status || "onbekend")}
                  {streamAan && v.status !== "ready" && (
                    <button onClick={() => verversStatus(v)} data-testid={`video-ververs-${v.id}`}
                      style={{ ...wegStijl, marginLeft: 8, borderColor: "var(--gold)", color: "var(--gold)" }}>
                      Verversen
                    </button>
                  )}
                </>
              ) : "—"}
            </span>
            <button onClick={() => verwijder(v)} data-testid={`video-weg-${v.id}`} style={wegStijl}>Weg</button>
          </li>
        ))}
      </ul>
    </AdminShell>
  );
}

/* ————— Fotogalerie ————— */
export function GalerijPage() {
  const { content, herlaad, fout, melden } = useAdminContent();
  const [fotos, setFotos] = useState(null);

  const lijst = fotos ?? content?.galerij_fotos ?? [];
  if (!content) return <AdminShell title="Fotogalerie"><div data-testid="galerij-laden">Laden…</div></AdminShell>;
  const media = mediaKaart(content.media);
  const uitgelicht = lijst.filter((f) => f.in_uitgelicht).length;

  const naUpload = async (nieuw) => {
    try {
      await api.post("/admin/galerij", { media_ids: nieuw.map((m) => m.id) });
      setFotos(null);
      await herlaad();
    } catch (e) { melden(e); }
  };

  const wissel = async (f) => {
    setFotos(lijst.map((x) => (x.id === f.id ? { ...x, in_uitgelicht: !x.in_uitgelicht } : x)));
    try {
      await api.put(`/admin/galerij/${f.id}`, { in_uitgelicht: !f.in_uitgelicht });
    } catch (e) { melden(e); setFotos(null); await herlaad(); }
  };

  const bijschrift = async (f) => {
    const tekst = window.prompt("Bijschrift (optioneel)", f.bijschrift || "");
    if (tekst === null) return;
    try {
      await api.put(`/admin/galerij/${f.id}`, { bijschrift: tekst });
      setFotos(null);
      await herlaad();
    } catch (e) { melden(e); }
  };

  const verwijder = async (f) => {
    if (!window.confirm("Deze foto uit de galerij halen?")) return;
    try {
      await api.delete(`/admin/galerij/${f.id}`);
      setFotos(null);
      await herlaad();
    } catch (e) { melden(e); }
  };

  const ordenen = async (nieuw) => {
    setFotos(nieuw);
    try {
      await api.post("/admin/galerij/reorder", { order: nieuw.map((f) => f.id) });
    } catch (e) { melden(e); setFotos(null); await herlaad(); }
  };

  return (
    <AdminShell title="Fotogalerie"
      actions={<Uploader testId="galerij-upload" label="Foto's toevoegen" onKlaar={naUpload} />}>
      <Titel onder="Sleep om te ordenen. Vink ‘uitgelicht’ aan voor het blok “De avond in beeld”; het ontwerp is gemaakt op 15 uitgelichte foto's.">
        Fotogalerie
      </Titel>
      <div data-testid="galerij-teller" style={{
        fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
        color: uitgelicht === 15 ? "var(--gold)" : "rgba(243,234,215,0.6)", marginBottom: 20,
      }}>
        {lijst.length} foto's · {uitgelicht} uitgelicht {uitgelicht === 15 ? "✓" : "(ontwerp: 15)"}
      </div>
      <Foutmelding fout={fout} />
      {lijst.length === 0 ? (
        <p style={{ fontFamily: "var(--sans)", fontSize: 14, opacity: 0.55 }}>
          Nog geen foto's. Gebruik “Foto's toevoegen” rechtsboven.
        </p>
      ) : (
        <Sleeplijst items={lijst} onOrde={ordenen} testId="galerij-lijst" render={(f) => (
          <>
            <Duim media={media[f.media_id]} hoogte={62} ratio="3 / 2" />
            <span style={{ flex: 1, fontFamily: "var(--sans)", fontSize: 13, opacity: 0.75 }}>
              {media[f.media_id]?.alt || media[f.media_id]?.bestandsnaam}
              {f.bijschrift ? ` · ${f.bijschrift}` : ""}
            </span>
            <label style={{
              display: "flex", alignItems: "center", gap: 8, fontFamily: "var(--mono)",
              fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase",
              color: f.in_uitgelicht ? "var(--gold)" : "rgba(243,234,215,0.5)",
            }}>
              <input type="checkbox" checked={f.in_uitgelicht} data-testid={`galerij-uitgelicht-${f.id}`}
                onChange={() => wissel(f)} />
              Uitgelicht
            </label>
            <button onClick={() => bijschrift(f)} data-testid={`galerij-bijschrift-${f.id}`}
              style={{ ...knopStijl, padding: "7px 10px", fontSize: 9 }}>Bijschrift</button>
            <button onClick={() => verwijder(f)} data-testid={`galerij-weg-${f.id}`} style={wegStijl}>Weg</button>
          </>
        )} />
      )}
    </AdminShell>
  );
}

/* ————— Personenlijst (inductees / bestuur) ————— */
export function PersonenPage({ soort }) {
  const { content, herlaad, fout, melden } = useAdminContent();
  const [items, setItems] = useState(null);

  const isInductee = soort === "inductees";
  const titel = isInductee ? "Inductees" : "Bestuur";
  const bron = content ? (isInductee ? content.inductees : content.bestuursleden) : [];
  const lijst = items ?? bron;

  if (!content) return <AdminShell title={titel}><div data-testid="personen-laden">Laden…</div></AdminShell>;
  const media = mediaKaart(content.media);

  const naam = (p) => (isInductee ? p.naam : `${p.voornaam} ${p.achternaam}`);

  const ordenen = async (nieuw) => {
    setItems(nieuw);
    try {
      await api.post(`/admin/${soort}/reorder`, { order: nieuw.map((p) => p.id) });
    } catch (e) { melden(e); setItems(null); await herlaad(); }
  };

  const wissel = async (p) => {
    setItems(lijst.map((x) => (x.id === p.id ? { ...x, zichtbaar: !x.zichtbaar } : x)));
    try {
      await api.put(`/admin/${soort}/${p.id}`, { velden: { zichtbaar: !p.zichtbaar } });
    } catch (e) { melden(e); setItems(null); await herlaad(); }
  };

  const toevoegen = async () => {
    const invoer = window.prompt(isInductee ? "Naam van de inductee" : "Achternaam van het bestuurslid", "");
    if (!invoer || !invoer.trim()) return;
    try {
      const velden = isInductee
        ? { naam: invoer, jaar: 2026, nummer: String(lijst.length + 1).padStart(2, "0") }
        : { achternaam: invoer, rol: "Bestuurslid", nummer: String(lijst.length + 1).padStart(2, "0") };
      await api.post(`/admin/${soort}`, { velden });
      setItems(null);
      await herlaad();
    } catch (e) { melden(e); }
  };

  const verwijder = async (p) => {
    if (!window.confirm(`${naam(p)} verwijderen?`)) return;
    try {
      await api.delete(`/admin/${soort}/${p.id}`);
      setItems(null);
      await herlaad();
    } catch (e) { melden(e); }
  };

  return (
    <AdminShell title={titel} actions={
      <button onClick={toevoegen} data-testid="persoon-toevoegen" style={knopStijl}>+ Toevoegen</button>
    }>
      <Titel onder={isInductee
        ? "Sleep om te herordenen. Een inductee met het veld ‘award’ gevuld verschijnt automatisch in het Starlight-blok onder het raster."
        : "Sleep om te herordenen. Klik een naam om de volledige biografie, de feitenregels en het portret te beheren."}>
        {titel}
      </Titel>
      <Foutmelding fout={fout} />
      <Sleeplijst items={lijst} onOrde={ordenen} testId="personen-lijst" render={(p) => (
        <>
          <Duim media={media[p.portret_id]} hoogte={64} />
          <Link to={`/admin/${soort}/${p.id}`} data-testid={`persoon-edit-${p.id}`} style={{
            flex: 1, fontFamily: "var(--serif)", fontSize: 19,
          }}>
            {naam(p)}
            {p.award ? <span style={{ color: "var(--gold)", fontSize: 13 }}> · {p.award}</span> : null}
          </Link>
          <button onClick={() => wissel(p)} data-testid={`persoon-toggle-${p.id}`} style={{
            fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase",
            color: p.zichtbaar ? "var(--gold)" : "rgba(243,234,215,0.5)", background: "transparent",
            border: "1px solid var(--line)", padding: "8px 12px",
          }}>{p.zichtbaar ? "Zichtbaar" : "Verborgen"}</button>
          <button onClick={() => verwijder(p)} data-testid={`persoon-weg-${p.id}`} style={wegStijl}>Weg</button>
        </>
      )} />
    </AdminShell>
  );
}
